//-- Copyright (c) 2021 TMT Insights Group, LLC
import {
  BaseTableConfiguration,
  ClientConfig,
  DashboardConfig,
  DataTableConfig,
  Exceptions,
  IngestManager,
  JobDetailTasks,
  Jobs,
  OrderPlacementConfig,
  Orders,
  Params,
  PendingAssets,
  Profiles,
  Tasks,
  TitleConfig,
  Titles,
  TransferQueue
} from '@/__configs/config_types';
import baseDataConfig from '../slices/baseDataConfig';
import { defaultGraphs, graphDirectoryKeys } from '@/components/Dashboard/DefaultGraphs';
import { deepMerge } from '@/_utils/utils';
import { FilterType } from '@/components/_common/Filters/FilterDictionary';
import { PAGE_SIZE } from '@/constants/constants';
import { Availold } from '../slices/slice_types';

// parseFns types
interface ParseFnsProps {
  dashboardConfig?: {
    parseDataFn?: DashboardConfig['parseDataFn'];
  };
  titleConfig?: {
    parseDataFn?: TitleConfig['parseDataFn'];
  };
  orderPlacementConfig?: {
    parseVersionsFn?: OrderPlacementConfig['parseVersionsFn'];
    parseFilesFn?: OrderPlacementConfig['parseFilesFn'];
    parseTitlesFn?: OrderPlacementConfig['parseTitlesFn'];
    parseTitleMetadataFn?: OrderPlacementConfig['parseTitleMetadataFn'];
  };
}

// since you can't and shouldn't store functions in the redux store
// but we want to keep all dataTables config data in a single location
// extract all the functions and init the rest in the store
export const parseFns: ParseFnsProps = {};
export const setConfigParseFns = (config: ClientConfig) => {
  if (config.orderPlacementConfig) {
    const { parseVersionsFn, parseFilesFn, parseTitlesFn, parseTitleMetadataFn } = config.orderPlacementConfig;
    parseFns.orderPlacementConfig = { parseVersionsFn, parseFilesFn, parseTitlesFn, parseTitleMetadataFn };
  }
  parseFns.dashboardConfig = { parseDataFn: config.dashboardConfig?.parseDataFn };
  parseFns.titleConfig = { parseDataFn: config.titleConfig?.parseDataFn };

  // grab every parseDataFn from the merged dataConfig
  const mergedDataConfig: DataTableConfig = deepMerge(baseDataConfig, config.dataConfig);

  Object.keys(mergedDataConfig).forEach((key: string) => {
    const { parseDataFn } = mergedDataConfig[key as keyof DataTableConfig] as BaseTableConfiguration<any, any>;
    parseFns[key as keyof ParseFnsProps] = { parseDataFn };
  });
};

export let notificationVersion = 'v1';
export const setNotificationsVersion = (config: ClientConfig) => {
  notificationVersion = config.notificationsConfig?.version || 'v1';
};

export const orderPlacementConfig: Partial<OrderPlacementConfig> = {
  entityTypes: [],
  fileSelectionTitle: () => null
};
export const setOrderPlacementConfig = (config: ClientConfig) => {
  if (config.orderPlacementConfig?.entityTypes) orderPlacementConfig.entityTypes = config.orderPlacementConfig.entityTypes;
};

export const dataTableInitialState: DataTableConfig = {
  orders: {} as Orders,
  jobs: {} as Jobs,
  tasks: {} as Tasks,
  pendingAssets: {} as PendingAssets,
  processingTasks: {} as Tasks,
  titles: {} as Titles,
  titlesV2: {} as Titles,
  fileIngest: {} as IngestManager,
  transferQueue: {} as TransferQueue,
  profiles: {} as Profiles,
  jobDetailTasks: {} as JobDetailTasks,
  titleDetailJobs: {} as Jobs,
  titleDetailAvails: {} as Availold,
  exceptions: {} as Exceptions
};
export const initDataTables = (clientDataConfig: DataTableConfig) => {
  // merge base data config with client data config
  const mergedDataConfig = deepMerge(baseDataConfig, clientDataConfig);

  Object.keys(mergedDataConfig).forEach((key: string) => {
    const configKey = key as keyof DataTableConfig;
    const dataToKeep = mergedDataConfig[configKey];
    delete dataToKeep.parseDataFn;

    dataTableInitialState[configKey] = {
      ...dataToKeep,
      data: [],
      filters: clientDataConfig[configKey]?.filters || mergedDataConfig[configKey].filters || ([] as FilterType[]),
      settings: {
        filters: clientDataConfig[configKey]?.preset?.filters || baseDataConfig[configKey]?.preset?.filters || {},
        sorting: mergedDataConfig[configKey].preset?.sorting || {
          id: 'id',
          order: 'asc'
        },
        currentPage: 1,
        disableGetData: mergedDataConfig[configKey].disableGetData || false
      },
      params: (clientDataConfig[configKey]?.params || baseDataConfig[configKey]?.params) as Params,
      pagination: {
        total: 0,
        page_size: PAGE_SIZE
      },
      enableGridViewToggle: clientDataConfig[configKey]?.enableGridViewToggle || false
    };
  });
  return dataTableInitialState;
};

export const baseGraphList = [
  {
    id: 'status-cards',
    fullRow: true,
    checked: true
  }
];
export let graphList: { id: string; checked: boolean; fullRow?: boolean }[] = [...baseGraphList];
export const setGraphList = (config: ClientConfig) => {
  const validEnabledGraphs = config.dashboardConfig?.graphList?.filter((graph) => graphDirectoryKeys.includes(graph.id));
  const newGraphList = validEnabledGraphs && validEnabledGraphs.length > 0 ? validEnabledGraphs : defaultGraphs;
  graphList = graphList.concat(newGraphList);
};
